<script>
import { mapState, mapActions, mapGetters } from "vuex";

import NavigationWrapper from "@/components/NavigationWrapper";
import AlertHeatmap from "@/components/AlertHeatmap";
import AlertEvents from "@/components/AlertEvents";
import AlertDescription from "@/pages/Alerts/Alert/Description";
import { formatHashrate, formatRelativeDate, formatDuration } from "@/utils";

export default {
  // Included components
  components: {
    NavigationWrapper,
    AlertHeatmap,
    AlertEvents,
    AlertDescription,
  },

  data() {
    return {
      // The ID of the alert to display
      alertId: this.alertId,
    };
  },

  computed: {
    // The alert object
    alert() {
      return this.alerts[this.alertId];
    },

    // Mapped state values
    ...mapGetters(["can"]),
    ...mapState(["alerts", "activeTeamId"]),
  },

  // When the component is created, get the alert
  async created() {
    this.alertId = this.$route.params.alertId;

    // Get this current alert
    await this.getAlert({ alertId: this.alertId });
  },

  methods: {
    // Hide the deletion confirmation modal
    hideDeleteModal() {
      this.$refs["delete-modal"].hide();
    },

    // Delete the alert and reroute to alerts page
    async doDeleteAlert() {
      this.hideDeleteModal();
      await this.deleteAlert({ alertId: this.alertId });
      await this.$router.replace("/alerts");
    },

    // Other helper methods
    formatRelativeDate,
    formatHashrate,
    formatDuration,
    ...mapActions(["getAlert", "deleteAlert"]),
  },
};
</script>

<template>
  <NavigationWrapper show-back-link default-back-link="/alerts">
    <template #breadcrumbs>
      <b-breadcrumb-item to="/alerts">Alerts</b-breadcrumb-item>
      <b-breadcrumb-item active>{{ alert.name }}</b-breadcrumb-item>
    </template>
    <b-row class="mb-3">
      <b-col cols="8">
        <h1>
          {{ alert.name }}
        </h1>
      </b-col>
      <b-col v-if="can.alerts.edit" cols="4" align-self="center">
        <span class="float-right">
          <b-button v-b-modal.modal-footer-sm class="mr-2" variant="danger">
            Delete
          </b-button>
          <b-modal
            ref="delete-modal"
            id="modal-footer-sm"
            title="Confirm delete"
            button-size="sm"
            hide-header-close
          >
            <p class="my-2">
              Are you sure you want to delete
              <b>{{ alert.name }}</b>
              ? Doing so will also delete all its events.
            </p>
            <template #modal-footer>
              <div class="float-right">
                <b-button class="mr-3" variant="light" @click="hideDeleteModal">
                  Cancel
                </b-button>
                <b-button variant="danger" @click="doDeleteAlert">
                  Delete
                </b-button>
              </div>
            </template>
          </b-modal>
          <b-button variant="primary" replace :to="`/alerts/${alertId}/edit`">
            Edit Alert
          </b-button>
        </span>
      </b-col>
      <b-col cols="12" class="Alert-description">
        <b-badge v-if="!alert.enabled" key="null">PAUSED</b-badge>
        <b-badge v-else-if="alert.triggering" key="true" variant="danger">
          ALERT
        </b-badge>
        <b-badge v-else variant="success" key="false">OK</b-badge>
        <span>&nbsp;</span>
        <AlertDescription :alert="alert" />
      </b-col>
    </b-row>
    <b-card class="mb-3">
      <div>
        <b>Notes:</b>
        {{ alert.notes }}
      </div>
      <div>
        <b>Last Triggered:</b>
        {{ formatRelativeDate(alert.last_triggered) }}
      </div>
    </b-card>
    <h3 class="mb-3">Events</h3>
    <AlertHeatmap :alert-id="alertId" class="mb-3"></AlertHeatmap>
    <AlertEvents :alert-id="alertId"></AlertEvents>
  </NavigationWrapper>
</template>

<style scoped></style>
